// Every name the Build group speaks to a student, in ONE place.
//
// Founder pick, 2026-09-27 (#2394, Set 1 over Set 2 "Make / Patent it" and
// Set 3 "Do something real"): the features under this group are named for
// what the student DOES, not for a place they go. The section is BUILD; the
// home is Brainstorm; the three steps are File a patent, Write a paper and
// Practice a pitch. The sidebar, the home, the three step pages, the shared
// door, the arc every surface lists, the unlocks kickers, the pricing line
// and the "answer them on ..." links all read from here, so a change of
// name is a change here and nowhere else. `buildNamesDrift.test.js` fails
// when a surface types one of these lines itself instead of reading it, and
// when the retired names ("Builder pack", "Builder track", "Labs", "Patent
// Lab", "Pitch Lab", "Research Lab") come back in visible copy.
//
// What this file does NOT govern, on purpose: identifiers. The `builder-*` / `pitch-*` / `study-*` routes and test ids,
// `/patent-lab` / `/pitch-lab` / `/research-lab`, the `builder_pack_*`
// events, `BuilderPackPage.jsx`, `PatentLabPage.jsx`, `builderPack.js` and
// `BuilderPackDoor` keep their first names whatever the copy says.
//
// Keep this module import-free: the production walks under
// tests/playwright-prod read it too, straight from the source tree.

const group = 'Build';
const home = 'Brainstorm';
const patent = 'File a patent';
const research = 'Write a paper';
const pitch = 'Practice a pitch';

/** "file a patent": a label with its first letter lowered, for mid-sentence use. */
export const lower = (s) => s.charAt(0).toLowerCase() + s.slice(1);

const LABEL = { brainstorm: home, patent, research, pitch };

/**
 * The steps listed the way a sentence lists them, for step keys in the
 * listing order: "Brainstorm, file a patent, write a paper, practice a
 * pitch". The first keeps its capital; the rest lower theirs.
 */
export function stepList(keys) {
    return keys.map((k, i) => (i === 0 ? LABEL[k] : lower(LABEL[k]))).join(', ');
}

const NUMBER = { 1: 'one', 2: 'two', 3: 'three', 4: 'four' };

/**
 * The landing band's heading, from the steps the band shows (the research
 * step only when its flag is on): "Brainstorm, file a patent, write a
 * paper, practice a pitch: one idea, three things a reader can check."
 */
export function landingHeading(keys) {
    return `${stepList(keys)}: one idea, ${NUMBER[keys.length - 1]} things a reader can check.`;
}

export const BUILD_NAMES = {
    // The sidebar section (the rail renders it in caps) and the nested list
    // of steps under the home.
    group,
    groupList: `${group} steps`,

    // The shared door a free account meets (BuilderPackDoor): the session's
    // own heading on the home, the steps' from any step ("File a patent,
    // write a paper, practice a pitch: with the Season Pass", from the steps
    // the door shows), and the list's accessible name.
    door: {
        home: `${home} opens with the Season Pass`,
        step: (keys) => `${stepList(keys)}: with the Season Pass`,
        list: `The steps under ${group}`,
    },

    // The home at /builder: the seven answers, once, and the path.
    home: {
        label: home,
        title: home,
        // Only the steps that read the seven: File a patent drafts from them
        // (builder.py) and Practice a pitch writes from them (pitch.py).
        // Write a paper asks its own seven (study.py), so the line never
        // says everything under Build reads them (#2688).
        subtitle: `Seven answers, in your words. ${patent} and ${pitch} start from them, and each step leaves a record a reader can check.`,
        // "Answer them on the Brainstorm page": the link every other step
        // shows under the read-only seven.
        where: `the ${home} page`,
        unlocks: `What ${home} unlocks`,
        learnMore: `How ${group} works`,
        shell: {
            loading: 'Loading your brainstorm...',
            failed: 'Your brainstorm could not be loaded',
        },
    },

    // The three steps, in listing order: patent, research, pitch. `label`
    // is the sidebar row and the arc's step name; `title` the page's hero;
    // `where` the page named mid-sentence; `unlocks` the page's kicker;
    // `shell` the two lines LabShell draws for its loading and
    // could-not-load states (an action name is not a noun those sentences
    // can take, so each step writes its own).
    steps: {
        patent: {
            label: patent,
            title: patent,
            subtitle: 'Take one thing you noticed and turn it into a provisional patent application, in your own words.',
            where: `the ${patent} page`,
            unlocks: `What ${patent} unlocks`,
            learnMore: 'How filing a patent works',
            shell: {
                loading: 'Loading your patent work...',
                failed: 'Your patent work could not be loaded',
            },
        },
        research: {
            label: research,
            title: research,
            subtitle: 'Pick an area from your own record, ask a question that is yours, and write a paper a mentor keeps asking about until it holds.',
            where: `the ${research} page`,
            unlocks: `What ${research} unlocks`,
            learnMore: 'How writing a paper works',
            shell: {
                loading: 'Loading your paper...',
                failed: 'Your paper could not be loaded',
            },
        },
        pitch: {
            label: pitch,
            title: pitch,
            subtitle: 'Turn your idea into a short pitch, practice it out loud, and get three fixes after every run.',
            where: `the ${pitch} page`,
            unlocks: `What ${pitch} unlocks`,
            learnMore: 'How practicing a pitch works',
            shell: {
                loading: 'Loading your pitch...',
                failed: 'Your pitch could not be loaded',
            },
        },
    },
};

// The listing order the sidebar nests the steps in. Presentational only:
// after Brainstorm the steps are peers, and no surface may read a sequence
// off this (founder 2026-09-28, #2396).
export const BUILD_STEP_ORDER = ['patent', 'research', 'pitch'];

export default BUILD_NAMES;
